import { ClockIcon } from '@heroicons/react/outline'
import classnames from 'classnames'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import {
  DATE_LOCALE,
  ENABLE_ARCHIVED_GAMES,
  ENABLE_MIGRATE_STATS,
} from '../../constants/settings'
import {
  ARCHIVE_GAMEDATE_TEXT,
  GUESS_DISTRIBUTION_TEXT,
} from '../../constants/strings'
import { fetchDistribution } from '../../lib/leaderboard'
import { GameStats, getStoredIsHighContrastMode } from '../../lib/localStorage'
import { solutionGameDate } from '../../lib/words'
import { Histogram } from '../stats/Histogram'
import { Leaderboard } from '../stats/Leaderboard'
import { MigrationIntro } from '../stats/MigrationIntro'
import { BaseModal } from './BaseModal'

// Last distribution shown, so a reopen (or a prefetch at game end) draws the
// chart immediately instead of "Loading distribution..." and then jumping.
let lastDistribution: number[] | null = null

export const prefetchDistribution = (): Promise<void> =>
  fetchDistribution()
    .then((d) => {
      lastDistribution = d
    })
    .catch(() => {})

type Props = {
  isOpen: boolean
  handleClose: () => void
  // Show with no fade-in (used when returning from the Save the Date card).
  openInstantly?: boolean
  solution: string
  guesses: string[]
  gameStats: GameStats
  solutionIndex: number
  solveTimeMs: number | null
  isLatestGame: boolean
  isGameLost: boolean
  // Opens the in-game Save the Date card.
  handleOpenSaveTheDate: () => void
  isGameWon: boolean
  handleShareToClipboard: () => void
  handleShareFailure: () => void
  handleMigrateStatsButton: () => void
  isHardMode: boolean
  isDarkMode: boolean
  isHighContrastMode: boolean
  numberOfGuessesMade: number
}

export const StatsModal = ({
  isOpen,
  handleClose,
  openInstantly = false,
  solution,
  guesses,
  gameStats,
  solutionIndex,
  solveTimeMs,
  isLatestGame,
  isGameLost,
  handleOpenSaveTheDate,
  isGameWon,
  handleShareToClipboard,
  handleShareFailure,
  handleMigrateStatsButton,
  isHardMode,
  isDarkMode,
  isHighContrastMode,
  numberOfGuessesMade,
}: Props) => {
  // Global guess distribution from the shared server DB (all players, all-time).
  const [distribution, setDistribution] = useState<number[] | null>(
    () => lastDistribution
  )
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    fetchDistribution()
      .then((d) => {
        lastDistribution = d
        if (!cancelled) setDistribution(d)
      })
      // Keep whatever is on screen if a refresh fails.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isOpen, isGameWon, isGameLost])

  const totalGames = distribution ? distribution.reduce((a, b) => a + b, 0) : 0

  return (
    <BaseModal
      title=""
      isOpen={isOpen}
      handleClose={handleClose}
      hideClose={isGameWon || isGameLost}
      instant={openInstantly}
    >
      <Leaderboard
        day={solutionIndex}
        isGameWon={isGameWon}
        isGameLost={isGameLost}
      />
      <h4 className="mt-6 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">
        {GUESS_DISTRIBUTION_TEXT}
      </h4>
      {distribution === null ? (
        <p className="m-2 text-sm text-gray-500 dark:text-gray-300">
          Loading distribution…
        </p>
      ) : totalGames === 0 ? (
        <p className="m-2 text-sm text-gray-500 dark:text-gray-300">
          No games recorded yet.
        </p>
      ) : (
        <Histogram
          isLatestGame={isLatestGame}
          winDistribution={distribution}
          isGameWon={isGameWon}
          isGameLost={isGameLost}
          numberOfGuessesMade={numberOfGuessesMade}
        />
      )}
      {(isGameLost || isGameWon) && ENABLE_ARCHIVED_GAMES && !isLatestGame && (
        <div className="mt-5 flex items-center justify-center text-center dark:text-white sm:mt-6">
          <div className="mt-2 inline-flex">
            <ClockIcon className="mr-1 mt-2 mt-1 h-5 w-5 stroke-black dark:stroke-white" />
            <div className="mt-1 ml-1 text-center text-sm sm:text-base">
              <strong>{ARCHIVE_GAMEDATE_TEXT}:</strong>
              <br />
              {format(solutionGameDate, 'd MMMM yyyy', {
                locale: DATE_LOCALE,
              })}
            </div>
          </div>
        </div>
      )}
      {(isGameWon || isGameLost) && (
        <button
          type="button"
          onClick={handleOpenSaveTheDate}
          className={classnames(
            'mt-6 block w-full rounded-md px-4 py-3 text-center text-base font-bold uppercase tracking-wide text-white shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800',
            getStoredIsHighContrastMode()
              ? 'bg-orange-700 hover:bg-orange-800 focus-visible:ring-orange-500'
              : 'bg-green-700 hover:bg-green-800 focus-visible:ring-green-500'
          )}
        >
          Continue to Save the Date →
        </button>
      )}
      {ENABLE_MIGRATE_STATS && (
        <div>
          <hr className="mt-4 -mb-4 border-gray-500" />
          <MigrationIntro handleMigrateStatsButton={handleMigrateStatsButton} />
        </div>
      )}
    </BaseModal>
  )
}
